import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';
import {
  getFirebaseVapidKey,
  getFirebaseWebConfig,
  isFirebaseWebPushConfigured,
} from '../config/firebaseWeb';
import {
  registerDeviceToken,
  unregisterDeviceToken,
} from '../services/deviceTokenService';

const FCM_TOKEN_STORAGE_KEY = 'volksign_fcm_web_token';

let messagingInstance = null;
let foregroundUnsubscribe = null;
let registerInFlight = null;

function getOrInitFirebaseApp() {
  const config = getFirebaseWebConfig();
  if (!config) return null;
  if (getApps().length) return getApps()[0];
  return initializeApp(config);
}

function getServiceWorkerUrl() {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}/firebase-messaging-sw.js`;
}

async function getMessagingRegistration() {
  if (!('serviceWorker' in navigator)) return null;
  const swUrl = getServiceWorkerUrl();
  const registration = await navigator.serviceWorker.register(swUrl);
  await navigator.serviceWorker.ready;
  return registration;
}

export function getStoredFcmToken() {
  try {
    return localStorage.getItem(FCM_TOKEN_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function storeFcmToken(token) {
  try {
    if (token) localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    else localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Request permission, get FCM token, POST /device-tokens.
 * Safe no-op when Firebase env is missing or browser unsupported.
 */
export async function registerWebPush({ onForegroundMessage } = {}) {
  if (!isFirebaseWebPushConfigured()) {
    return { ok: false, reason: 'not_configured' };
  }
  if (registerInFlight) return registerInFlight;

  registerInFlight = (async () => {
    try {
      if (!(await isSupported())) {
        return { ok: false, reason: 'unsupported' };
      }

      if (typeof Notification === 'undefined') {
        return { ok: false, reason: 'no_notification_api' };
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return { ok: false, reason: 'permission_denied', permission };
      }

      const app = getOrInitFirebaseApp();
      if (!app) return { ok: false, reason: 'not_configured' };

      const registration = await getMessagingRegistration();
      messagingInstance = getMessaging(app);

      const vapidKey = getFirebaseVapidKey();
      const token = await getToken(messagingInstance, {
        vapidKey,
        serviceWorkerRegistration: registration || undefined,
      });

      if (!token) {
        return { ok: false, reason: 'no_token' };
      }

      const previous = getStoredFcmToken();
      if (previous && previous !== token) {
        try {
          await unregisterDeviceToken(previous);
        } catch {
          // continue — new token still needs register
        }
      }

      const response = await registerDeviceToken({
        token,
        platform: 'web',
        deviceName: navigator.userAgent.slice(0, 120),
        appVersion: process.env.REACT_APP_VERSION || 'web',
      });

      storeFcmToken(token);

      if (foregroundUnsubscribe) {
        foregroundUnsubscribe();
        foregroundUnsubscribe = null;
      }
      if (typeof onForegroundMessage === 'function') {
        foregroundUnsubscribe = onMessage(messagingInstance, onForegroundMessage);
      }

      return {
        ok: true,
        token,
        pushEnabled: response?.push_enabled ?? response?.data?.push_enabled ?? null,
        response,
      };
    } catch (error) {
      console.warn('Web push registration failed:', error);
      return { ok: false, reason: 'error', error };
    } finally {
      registerInFlight = null;
    }
  })();

  return registerInFlight;
}

/**
 * DELETE /device-tokens for the stored FCM token (call on logout).
 */
export async function unregisterWebPush() {
  const token = getStoredFcmToken();
  try {
    if (token) {
      await unregisterDeviceToken(token);
    }
  } catch (error) {
    console.warn('Web push unregister failed:', error);
  }

  try {
    if (messagingInstance) {
      await deleteToken(messagingInstance);
    }
  } catch {
    // ignore
  }

  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
    foregroundUnsubscribe = null;
  }
  storeFcmToken(null);
  messagingInstance = null;
}
